<template>
  <section class="py-16 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      <!-- Section Header -->
      <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
        <div>
          <p class="text-primary-500 text-sm font-semibold uppercase tracking-widest mb-2">Hand-picked for you</p>
          <h2 class="text-3xl md:text-4xl font-bold text-neutral-800">
            Featured Properties
          </h2>
        </div>
        <router-link
          to="/properties"
          class="inline-flex items-center gap-2 text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors group"
        >
          View All Properties
          <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </router-link>
      </div>

      <!-- Loading skeletons -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
        <div v-for="n in 6" :key="n" class="bg-white rounded-2xl border border-neutral-100 shadow-sm overflow-hidden animate-pulse">
          <div class="h-52 bg-neutral-200"></div>
          <div class="p-5 space-y-3">
            <div class="h-4 bg-neutral-200 rounded w-3/4"></div>
            <div class="h-3 bg-neutral-100 rounded w-1/2"></div>
            <div class="h-3 bg-neutral-100 rounded w-full"></div>
            <div class="h-9 bg-neutral-100 rounded-xl mt-2"></div>
          </div>
        </div>
      </div>

      <!-- Properties Grid -->
      <div v-else-if="featuredProperties.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
        <div
          v-for="property in featuredProperties"
          :key="property.id"
          class="group bg-white rounded-2xl border border-neutral-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
        >
          <!-- Image with zoom effect -->
          <div class="relative overflow-hidden h-52">
            <img
              v-if="property.image"
              :src="property.image"
              :alt="property.title"
              class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div v-else class="w-full h-full bg-neutral-100 flex items-center justify-center">
              <svg class="w-12 h-12 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <!-- Gradient overlay -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

            <!-- Type badge — top left -->
            <span class="absolute top-3 left-3 bg-primary-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
              {{ property.type }}
            </span>

            <!-- Price badge — bottom left -->
            <span class="absolute bottom-3 left-3 bg-white text-neutral-800 text-sm font-bold px-3 py-1.5 rounded-lg shadow">
              RWF {{ property.price.toLocaleString() }}
            </span>

            <!-- Wishlist button — top right -->
            <button class="absolute top-3 right-3 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow transition-colors">
              <svg class="w-4 h-4 text-neutral-500 hover:text-red-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
          </div>

          <!-- Card Body -->
          <div class="p-5">

            <!-- Title -->
            <h3 class="text-base font-bold text-neutral-800 mb-1 group-hover:text-primary-500 transition-colors line-clamp-1">
              {{ property.title }}
            </h3>

            <!-- Location -->
            <p class="flex items-center gap-1.5 text-sm text-neutral-400 mb-4">
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {{ property.location }}
            </p>

            <!-- Divider -->
            <div class="border-t border-neutral-100 mb-4"></div>

            <!-- Features row -->
            <div class="flex items-center justify-between text-xs text-neutral-500 mb-4">
              <!-- Plot: show area only -->
              <template v-if="property.type === 'Plot'">
                <div class="flex items-center gap-1.5 text-emerald-600">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <span class="font-semibold">Land Plot</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  <span>{{ property.size ? property.size + ' m²' : '—' }}</span>
                </div>
              </template>
              <!-- Other types: beds / baths / size -->
              <template v-else>
                <div class="flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>{{ property.bedrooms }} Beds</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h8m-8 5h8m-4-9v14" />
                  </svg>
                  <span>{{ property.bathrooms }} Baths</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  <span>{{ property.size ? property.size + ' m²' : '—' }}</span>
                </div>
              </template>
            </div>

            <!-- CTA -->
            <router-link
              :to="`/property/${property.id}`"
              class="block w-full text-center py-2.5 rounded-xl text-sm font-semibold text-primary-500 border border-primary-500 hover:bg-primary-500 hover:text-white transition-all duration-200"
            >
              View Details
            </router-link>
          </div>
        </div>
      </div>

      <!-- Empty state (fallback) -->
      <div v-else class="text-center py-16 text-neutral-400 text-sm">
        No featured properties available at the moment.
      </div>

    </div>
  </section>
</template>

<script lang="ts">
/* eslint-disable camelcase */
import { Options, Vue } from 'vue-class-component'
import { propertiesApi, Property } from '@/api/properties'

interface UiProperty {
  id: number
  title: string
  price: number
  location: string
  type: string
  bedrooms: string
  bathrooms: string
  size: number | null
  image: string
}

@Options({})
export default class FeaturedProperties extends Vue {
  loading = true
  featuredProperties: UiProperty[] = []

  async created () {
    try {
      const res = await propertiesApi.list({ sort: 'popular', limit: 6 })
      this.featuredProperties = res.data.data.map((p: Property) => ({
        id: p.id,
        title: p.title,
        price: p.price,
        location: [p.sector, p.district].filter(Boolean).join(', '),
        type: p.property_type,
        bedrooms: p.bedrooms,
        bathrooms: p.bathrooms,
        size: p.size || null,
        image: p.images?.[0] || ''
      }))
    } catch { } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
